import { useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import SignatureCanvas from "react-signature-canvas";
import { Pen, CheckCircle, X } from "lucide-react";
import { toast } from "sonner";

export const JobDetailSignature = ({
  job,
  isMechanic,
  canEdit,
  signatureDialogOpen,
  setSignatureDialogOpen,
  onSaveSignature,
}) => {
  const sigPadRef = useRef(null);

  // Only show for mechanics after check-in, or for admin/manager, or if signature exists
  const shouldShow =
    (isMechanic && job.check_in) ||
    (!isMechanic && canEdit) ||
    job.customer_signature;

  if (!shouldShow) return null;

  const handleClearSignature = () => {
    sigPadRef.current?.clear();
  };

  const handleSaveSignature = () => {
    if (sigPadRef.current?.isEmpty()) {
      toast.error("Please provide a signature");
      return;
    }
    const signatureData = sigPadRef.current.toDataURL();
    onSaveSignature(signatureData);
    setSignatureDialogOpen(false);
  };

  return (
    <Card className="border-slate-200 shadow-sm rounded-sm" data-testid="signature-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
          <Pen className="w-5 h-5" />
          Customer Signature
          {isMechanic && job.status !== "completed" && !job.customer_signature && (
            <span className="text-red-500 text-sm font-normal">* Required for checkout</span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {job.customer_signature ? (
          <div>
            <img
              src={job.customer_signature}
              alt="Customer signature"
              className="border rounded-sm bg-white max-h-32"
            />
            <p className="text-xs text-slate-500 mt-1">
              Signed: {job.signed_at ? new Date(job.signed_at).toLocaleString() : "Unknown"}
            </p>
          </div>
        ) : (
          <div>
            {canEdit && job.status !== "completed" && (
              <Dialog open={signatureDialogOpen} onOpenChange={setSignatureDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="rounded-sm"
                    data-testid="capture-signature-btn"
                  >
                    <Pen className="w-4 h-4 mr-2" />
                    Capture Signature
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-sm max-w-md">
                  <DialogHeader>
                    <DialogTitle>Customer Signature</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="border rounded-sm bg-white" style={{ width: '100%', height: '200px' }}>
                      <SignatureCanvas
                        ref={sigPadRef}
                        canvasProps={{
                          width: 400,
                          height: 200,
                          className: "signature-canvas",
                          style: { width: '100%', height: '100%' }
                        }}
                        backgroundColor="white"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={handleClearSignature}
                        className="flex-1 rounded-sm"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Clear
                      </Button>
                      <Button
                        onClick={handleSaveSignature}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 rounded-sm"
                        data-testid="save-signature-btn"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Save Signature
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
            {!canEdit && <p className="text-slate-400 text-sm">No signature captured</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default JobDetailSignature;
